/**
 * The filter control that every page uses.
 *
 * A row of select boxes above a list costs the same screen space whether or
 * not anybody is filtering, and it pushes the list itself below the fold. The
 * filters live behind one button instead. The button carries the number of
 * filters in use, so a narrowed list never looks like an empty one — the
 * commonest confusion a hidden filter causes.
 *
 * One button, one panel, the same place on every page. A page with a single
 * filter and a page with six look and behave the same.
 */

import { useRef, useState } from "react";

import { Badge, Button, classes, inputClass } from "./primitives.tsx";
import { useDismiss } from "./use-dismiss.ts";

export interface FilterChoice {
  readonly value: string;
  readonly label: string;
}

export interface FilterSpec {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  /** The value that means "do not filter by this". The first choice by default. */
  readonly anyValue?: string;
  readonly choices: readonly FilterChoice[];
  readonly onChange: (value: string) => void;
}

/** Whether one filter is narrowing the list. */
function isActive(filter: FilterSpec): boolean {
  const any = filter.anyValue ?? filter.choices[0]?.value ?? "";
  return filter.value !== any;
}

export function FilterFlyout({
  filters,
  label = "Filter",
}: {
  readonly filters: readonly FilterSpec[];
  readonly label?: string;
}) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  useDismiss(container, open, () => setOpen(false));

  if (filters.length === 0) {
    return null;
  }
  const active = filters.filter(isActive);

  return (
    <div className="relative" ref={container}>
      <Button
        variant={active.length > 0 ? "primary" : "secondary"}
        size="small"
        onClick={() => setOpen(!open)}
      >
        <span>{label}</span>
        {active.length > 0 && <Badge tone="quiet">{active.length}</Badge>}
      </Button>
      {open && (
        <div
          data-testid="filter-flyout"
          className={classes(
            "absolute right-0 z-20 mt-1 w-72 rounded-arc border border-subtle",
            "bg-paper p-3 shadow-lg",
          )}
        >
          <div className="flex flex-col gap-3">
            {filters.map((filter) => (
              <label key={filter.key} className="flex flex-col gap-1 text-sm">
                <span className="font-medium">{filter.label}</span>
                <select
                  aria-label={filter.label}
                  className={inputClass}
                  value={filter.value}
                  onChange={(event) => filter.onChange(event.target.value)}
                >
                  {filter.choices.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {active.length > 0 && (
            <div className="mt-3 border-t border-subtle pt-3">
              <Button
                variant="quiet"
                size="small"
                onClick={() => {
                  for (const filter of filters) {
                    filter.onChange(filter.anyValue ?? filter.choices[0]?.value ?? "");
                  }
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The line that says a filter is hiding rows.
 *
 * A person who has forgotten a filter reads an empty list as "there is
 * nothing here". This says otherwise, and offers the way back.
 */
export function FilteredNote({
  shown,
  total,
  onClear,
}: {
  readonly shown: number;
  readonly total: number;
  readonly onClear: () => void;
}) {
  if (shown === total) {
    return null;
  }
  return (
    <p className="text-sm text-muted">
      {shown} of {total} shown.{" "}
      <Button variant="link" onClick={onClear}>
        Clear the filters
      </Button>
      .
    </p>
  );
}
